// ============================================
// B2：3D 视口内组件就地编辑 —— 坐标换算工具（纯函数，便于单测）
// 屏幕位移 → 页面坐标：叠加层分轴缩放 scaleX/scaleY × 锚点缩放 anchorScale
// ============================================

export interface OverlayScale {
  scaleX: number;
  scaleY: number;
  anchorScale?: number;
}

export function screenToPageDelta(dx: number, dy: number, s: OverlayScale): { x: number; y: number } {
  const kx = (s.scaleX || 1) * (s.anchorScale || 1);
  const ky = (s.scaleY || 1) * (s.anchorScale || 1);
  return { x: dx / kx, y: dy / ky };
}

/** 位置钳制到页面内 */
export function clampPosition(
  x: number,
  y: number,
  size: { width: number; height: number },
  page: { width: number; height: number },
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(Math.round(x), Math.max(0, page.width - size.width))),
    y: Math.max(0, Math.min(Math.round(y), Math.max(0, page.height - size.height))),
  };
}

/** 尺寸钳制（最小 20px，最大不超过页面） */
export function clampSize(
  width: number,
  height: number,
  page: { width: number; height: number },
  maxW = page.width,
  maxH = page.height,
): { width: number; height: number } {
  return {
    width: Math.max(20, Math.min(Math.round(width), Math.max(20, maxW))),
    height: Math.max(20, Math.min(Math.round(height), Math.max(20, maxH))),
  };
}

/** 手柄缩放：根据手柄方向计算新位置与尺寸（各轴独立处理） */
export function applyResize(
  handle: 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w',
  start: { x: number; y: number; width: number; height: number },
  delta: { x: number; y: number },
  page: { width: number; height: number },
): { x: number; y: number; width: number; height: number } {
  let x = start.x;
  let y = start.y;
  let width = start.width;
  let height = start.height;

  if (handle.includes('e')) {
    const maxW = page.width - start.x;
    width = Math.max(20, Math.min(Math.round(start.width + delta.x), Math.max(20, maxW)));
  }
  if (handle.includes('w')) {
    const right = start.x + start.width;
    width = Math.max(20, Math.round(start.width - delta.x));
    x = right - width;
    const minLeft = Math.max(0, right - page.width);
    if (x < minLeft) { x = minLeft; width = Math.max(20, right - x); }
  }
  if (handle.includes('s')) {
    const maxH = page.height - start.y;
    height = Math.max(20, Math.min(Math.round(start.height + delta.y), Math.max(20, maxH)));
  }
  if (handle.includes('n')) {
    const bottom = start.y + start.height;
    height = Math.max(20, Math.round(start.height - delta.y));
    y = bottom - height;
    const minTop = Math.max(0, bottom - page.height);
    if (y < minTop) { y = minTop; height = Math.max(20, bottom - y); }
  }

  return { x: Math.round(x), y: Math.round(y), width, height };
}

/** 框选相交判断 */
export function intersects(
  rect: { x1: number; y1: number; x2: number; y2: number },
  comp: { x: number; y: number; width: number; height: number },
): boolean {
  const left = Math.min(rect.x1, rect.x2);
  const right = Math.max(rect.x1, rect.x2);
  const top = Math.min(rect.y1, rect.y2);
  const bottom = Math.max(rect.y1, rect.y2);
  return !(comp.x + comp.width < left || comp.x > right || comp.y + comp.height < top || comp.y > bottom);
}
